import axios from 'axios'
import { ElMessage } from 'element-plus'
import CryptoJS from 'crypto-js'
import { currentConfig } from '@/config/api'

// API Configuration
const API_BASE_URL = currentConfig.WEB_API.BASE_URL
const MOBILE_API_BASE_URL = currentConfig.MOBILE_API.BASE_URL

// Create axios instances
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: currentConfig.WEB_API.TIMEOUT,
  headers: currentConfig.WEB_API.HEADERS
})

const mobileApiClient = axios.create({
  baseURL: MOBILE_API_BASE_URL,
  timeout: currentConfig.MOBILE_API.TIMEOUT,
  headers: currentConfig.MOBILE_API.HEADERS
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

mobileApiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
const handleResponse = (response) => {
  if (response.data && response.data.didError) {
    ElMessage.error(response.data.errorMessage || 'An error occurred')
    return Promise.reject(response.data)
  }
  return response.data // Return response.data as before
}

const handleError = (error) => {
  if (error.response?.status === 401) {
    localStorage.removeItem('authToken')
    localStorage.removeItem('user')
    window.location.href = '/#/login'
  }
  ElMessage.error(error.response?.data?.errorMessage || error.message || 'Network error')
  return Promise.reject(error)
}

apiClient.interceptors.response.use(handleResponse, handleError)
mobileApiClient.interceptors.response.use(handleResponse, handleError)

// Authentication API
export const authAPI = {
  // Web API Authentication
  login: (credentials) => apiClient.post('/auth/login', credentials),
  register: (userData) => apiClient.post('/auth/register', userData),
  logout: () => apiClient.post('/auth/logout'),
  refreshToken: () => apiClient.post('/auth/refresh-token'),
  
  // Mobile API Authentication
  mobileLogin: (credentials) => {
    // Hash the password using SHA1 (same as backend SecurityService.GetSha1)
    const sha1Hash = CryptoJS.SHA1(credentials.password).toString(CryptoJS.enc.Hex).toUpperCase();
    
    return mobileApiClient.post('/User/signIn', {
      UserName: credentials.username,
      Password: sha1Hash,
      AccessType: 2, // 2 for mobile app
      RememberMe: credentials.rememberMe || false,
      FirebaseTokenId: credentials.firebaseTokenId || ''
    });
  },
  mobileLogout: (token) => mobileApiClient.get('/User/Logout', { params: { Token: token } }),
  getCurrentUser: () => mobileApiClient.get('/User/getCurrentUser'),
  
  // Password Reset API
  getCaptchaId: () => mobileApiClient.get('/User/getCaptchaId'),
  getCaptchaPhoto: (captchaId) => mobileApiClient.get(`/User/getCaptchaPhoto/${captchaId}`),
  verifyFirstStep: (data) => mobileApiClient.post('/User/VerifyFirstStep', data),
  verifySecondStep: (data) => mobileApiClient.post('/User/VerifySecondStep', data),
  verifyFinalStep: (data) => mobileApiClient.post('/User/VerifyFinalStep', data),
  changePassword: (data) => mobileApiClient.post('/User/ChangePD', data)
}

// Leave Management API
export const leaveAPI = {
  // Web API
  getLeaveRequests: (params) => apiClient.get('/recruitment/leave-requests', { params }),
  createLeaveRequest: (data) => apiClient.post('/recruitment/leave-requests', data),
  updateLeaveRequest: (id, data) => apiClient.put(`/recruitment/leave-requests/${id}`, data),
  deleteLeaveRequest: (id) => apiClient.delete(`/recruitment/leave-requests/${id}`),
  approveLeaveRequest: (id, data) => apiClient.post(`/recruitment/leave-requests/${id}/approve`, data),
  rejectLeaveRequest: (id, data) => apiClient.post(`/recruitment/leave-requests/${id}/reject`, data),
  
  // Mobile API
  mobileTakeLeave: (data) => mobileApiClient.post('/Leave/takeLeave', data),
  mobileApproveLeave: (data) => mobileApiClient.post('/Leave/ApproveLeave', data),
  mobileCancelLeave: (leaveId) => mobileApiClient.delete(`/Leave/removeLeave/${leaveId}`),
  mobileGetLeaveTypes: () => mobileApiClient.get('/Leave/listLeaveType'),
  mobileGetWorkShifts: () => mobileApiClient.get('/Leave/listCodeWorkShift'),
  mobileGetLeaveHistory: (empId) => mobileApiClient.get(`/Leave/listLeavesByEmpId/${empId}`),
  mobileGetPendingLeaves: (empId) => mobileApiClient.get(`/Leave/listLeavesPendingByEmpId/${empId}`),
  mobileGetLeaveForApproval: (empId) => mobileApiClient.get(`/Leave/listLeaveForApprovalAsync/${empId}`),
  mobileGetLeaveAllowance: (empId, yearId) => mobileApiClient.get(`/Leave/listleaveAllowanceByEmpIdAsync/${empId}/${yearId}`),
  mobileUploadDocument: (formData) => mobileApiClient.post('/Documents/uploadDocument', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

// Timesheet/Attendance API
export const timesheetAPI = {
  // Web API
  getTimesheetRecords: (params) => apiClient.get('/recruitment/timesheet-records', { params }),
  createTimesheetRecord: (data) => apiClient.post('/recruitment/timesheet-records', data),
  updateTimesheetRecord: (id, data) => apiClient.put(`/recruitment/timesheet-records/${id}`, data),
  deleteTimesheetRecord: (id) => apiClient.delete(`/recruitment/timesheet-records/${id}`),
  
  // Mobile API
  mobileCheckInOut: (data) => mobileApiClient.post('/TimeSheet/checkInOut', data),
  mobileGetInOutStatus: () => mobileApiClient.get('/TimeSheet/getMyInOutStatus'),
  mobileGetTimesheetHistory: (fromDate, toDate) => mobileApiClient.get('/TimeSheet/getMyTimeSheetHistory', {
    params: { fromdate: fromDate, todate: toDate }
  }),
  mobileGetTimesheetForApproval: () => mobileApiClient.get('/TimeSheet/getTimeSheetForApproval'),
  mobileApproveCheckInOut: (data) => mobileApiClient.post('/TimeSheet/ApproveCheckInOut', data),
  mobileRejectCheckInOut: (data) => mobileApiClient.post('/TimeSheet/RejectCheckInOut', data),
  
  // Location validation API
  validateLocation: (data) => apiClient.post('/TimeSheet/validateLocation', data),
  getLocationSettings: (employeeId) => apiClient.get(`/TimeSheet/getLocationSettings/${employeeId}`)
}

// Recruitment API
export const recruitmentAPI = {
  // Web API
  getManpowerRequests: (params) => apiClient.get('/recruitment/manpower-requests', { params }),
  createManpowerRequest: (data) => apiClient.post('/recruitment/manpower-requests', data),
  updateManpowerRequest: (id, data) => apiClient.put(`/recruitment/manpower-requests/${id}`, data),
  deleteManpowerRequest: (id) => apiClient.delete(`/recruitment/manpower-requests/${id}`),
  approveManpowerRequest: (id, data) => apiClient.post(`/recruitment/manpower-requests/${id}/approve`, data),
  rejectManpowerRequest: (id, data) => apiClient.post(`/recruitment/manpower-requests/${id}/reject`, data),
  
  getJobAnnouncements: (params) => apiClient.get('/recruitment/job-announcements', { params }),
  createJobAnnouncement: (data) => apiClient.post('/recruitment/job-announcements', data),
  updateJobAnnouncement: (id, data) => apiClient.put(`/recruitment/job-announcements/${id}`, data),
  deleteJobAnnouncement: (id) => apiClient.delete(`/recruitment/job-announcements/${id}`),
  
  getJobApplications: (params) => apiClient.get('/recruitment/job-applications', { params }),
  createJobApplication: (data) => apiClient.post('/recruitment/job-applications', data),
  updateJobApplication: (id, data) => apiClient.put(`/recruitment/job-applications/${id}`, data),
  deleteJobApplication: (id) => apiClient.delete(`/recruitment/job-applications/${id}`),
  shortlistApplication: (id, data) => apiClient.post(`/recruitment/job-applications/${id}/shortlist`, data),
  
  getInterviews: (params) => apiClient.get('/recruitment/interviews', { params }),
  createInterview: (data) => apiClient.post('/recruitment/interviews', data),
  updateInterview: (id, data) => apiClient.put(`/recruitment/interviews/${id}`, data),
  deleteInterview: (id) => apiClient.delete(`/recruitment/interviews/${id}`),
  
  getRecruitmentDashboard: () => apiClient.get('/recruitment/dashboard'),
  
  // Mobile API
  mobileGetJobAnnouncements: () => mobileApiClient.get('/Recruitment/getJobAnnouncements'),
  mobileApplyForJob: (data) => mobileApiClient.post('/Recruitment/applyForJob', data),
  mobileGetMyApplications: (empId) => mobileApiClient.get(`/Recruitment/getMyApplications/${empId}`),
  mobileGetInterviewSchedule: (empId) => mobileApiClient.get(`/Recruitment/getInterviewSchedule/${empId}`)
}

// Employee Management API
export const employeeAPI = {
  // Web API
  getEmployees: (params) => apiClient.get('/employees', { params }),
  getEmployee: (id) => apiClient.get(`/employees/${id}`),
  createEmployee: (data) => apiClient.post('/employees', data),
  updateEmployee: (id, data) => apiClient.put(`/employees/${id}`, data),
  deleteEmployee: (id) => apiClient.delete(`/employees/${id}`),
  
  // Mobile API
  mobileGetEmployeeProfile: (empId) => mobileApiClient.get(`/Employee/getEmployeeProfile/${empId}`),
  mobileUpdateEmployeeProfile: (data) => mobileApiClient.post('/Employee/updateEmployeeProfile', data),
  mobileGetEmployeeDocuments: (empId) => mobileApiClient.get(`/Employee/getEmployeeDocuments/${empId}`),
  mobileSearchEmployeesForHandover: (searchText) => mobileApiClient.get(`/Employee/searchEmployeesForHandover/${searchText}`),
  mobileGetEmployeeSalary: (empId) => mobileApiClient.get(`/Employee/getEmployeeSalary/${empId}`),
  mobileGetEmployeeAllowance: (empId) => mobileApiClient.get(`/Employee/getEmployeeAllowance/${empId}`),
  mobileGetEmployeeContract: (empId) => mobileApiClient.get(`/Employee/getEmployeeContract/${empId}`)
}

// Notification API
export const notificationAPI = {
  // Web API
  getNotifications: (params) => apiClient.get('/notifications', { params }),
  markAsRead: (id) => apiClient.put(`/notifications/${id}/read`),
  markAllAsRead: () => apiClient.put('/notifications/mark-all-read'),
  deleteNotification: (id) => apiClient.delete(`/notifications/${id}`),
  
  // Mobile API
  mobileGetNotifications: (empId) => mobileApiClient.get('/Notification/list'),
  mobileMarkNotificationRead: (notificationId) => mobileApiClient.post(`/Notification/markAsRead/${notificationId}`),
  mobileGetUnreadCount: (empId) => mobileApiClient.get(`/Notification/getUnreadCount/${empId}`),
  mobileMarkAllAsRead: () => mobileApiClient.post('/Notification/markAllAsRead'),
  mobileDeleteNotification: (notificationId) => mobileApiClient.delete(`/Notification/delete/${notificationId}`),
  mobileGetNotificationStats: () => mobileApiClient.get('/Notification/stats'),
  mobileGetPendingApprovals: (moduleId) => mobileApiClient.get('/Notification/pendingApprovals', { params: { moduleId } }),
  mobileGetMyRequests: (moduleId) => mobileApiClient.get('/Notification/myRequests', { params: { moduleId } })
}

// Dashboard API
export const dashboardAPI = {
  getDashboardData: () => apiClient.get('/dashboard'),
  getKPIData: (params) => apiClient.get('/dashboard/kpi', { params }),
  getAttendanceStats: (params) => apiClient.get('/dashboard/attendance', { params }),
  getLeaveStats: (params) => apiClient.get('/dashboard/leave', { params })
}

// Document Management API
export const documentAPI = {
  // Upload document using the new endpoint
  uploadDocument: (formData) => mobileApiClient.post('/Documents/UploadDocumentFile', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  
  // Get document by ID
  getDocument: (documentId) => mobileApiClient.get(`/Documents/${documentId}`),
  
  // Delete document
  deleteDocument: (documentId) => mobileApiClient.delete(`/Documents/delete_document/${documentId}`),
  
  // Get documents by user
  getDocumentsByUser: (userId) => mobileApiClient.get(`/Documents/user/${userId}`),
  
  // Get documents by type
  getDocumentsByType: (documentType) => mobileApiClient.get(`/Documents/type/${documentType}`),
  
  // Download document file
  downloadDocumentFile: (documentId) => mobileApiClient.get(`/Documents/DownloadDocumentFile/${documentId}`, {
    responseType: 'blob'
  }),
  
  // Download profile photo
  downloadProfilePhoto: (documentId) => mobileApiClient.get(`/Documents/DownloadProfilePhoto/${documentId}`, {
    responseType: 'blob'
  }),
  
  // List all documents
  listDocuments: () => mobileApiClient.get('/Documents/list_documentAsync'),
  
  // Get documents by employee ID
  getDocumentsByEmployee: (empId) => mobileApiClient.get(`/Documents/ListproofdocumentbyEmployeeIDAsynch/${empId}`),
  
  // Get documents by proof ID
  getDocumentsByProofId: (proofId) => mobileApiClient.get(`/Documents/ListproofdocumentbyproofIDAsynch/${proofId}`),
  
  // Get documents by proof type
  getDocumentsByProofType: (proofTypeId) => mobileApiClient.get(`/Documents/ListproofdocumentbyProofTypeIDAsync/${proofTypeId}`),
  
  // Get documents by employee and type
  getDocumentsByEmployeeAndType: (empId, proofTypeId) => mobileApiClient.get(`/Documents/ListproofdocumentbyEmployeeAndTypeAsync/${empId}/${proofTypeId}`),
  
  // Get documents by employee and reference number
  getDocumentsByEmployeeAndReference: (empId, referenceNo) => mobileApiClient.get(`/Documents/ListproofdocumentbyEmployeeAndReferenceNoAsync/${empId}/${referenceNo}`),
  
  // Get documents by employee, reference number and type
  getDocumentsByEmployeeReferenceAndType: (empId, referenceNo, proofTypeId) => mobileApiClient.get(`/Documents/getproofdocumentbyEmployeeIDandReferenceNoandTypeAsync/${empId}/${referenceNo}/${proofTypeId}`)
}

// Standard Data API
export const standardDataAPI = {
  // Mobile API
  mobileGetDepartments: () => mobileApiClient.get('/StandardData/getDepartments'),
  mobileGetPositions: () => mobileApiClient.get('/StandardData/getPositions'),
  mobileGetLeaveTypes: () => mobileApiClient.get('/StandardData/getLeaveTypes'),
  mobileGetWorkShifts: () => mobileApiClient.get('/StandardData/getWorkShifts'),
  mobileGetDocumentProofTypes: () => mobileApiClient.get('/StandardData/getDocumentProofTypeAsync')
}

// File Upload API
export const fileAPI = {
  uploadFile: (file, type) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)
    
    return apiClient.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },
  
  mobileUploadFile: (file, type) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)
    
    return mobileApiClient.post('/Documents/uploadFile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
}

// Translation API
export const translationAPI = {
  // Mobile API
  mobileGetTranslationsByPlatform: (platform) => mobileApiClient.get(`/Translation/listTranslationByPlatform/${platform}`),
  mobileGetTranslationsByKey: (key, platform) => mobileApiClient.get(`/Translation/listTranslationByKey/${key}/${platform}`),
  mobileGetTranslationsByResourceKey: (resourceKey, platform) => mobileApiClient.get(`/Translation/listTranslationByResourceKey/${resourceKey}/${platform}`),
  mobileGetTranslationsByPlatformWithoutMenu: (platform) => mobileApiClient.get(`/Translation/listTranslationByPlatformWithoutMenu/${platform}`),
  mobileAddTranslation: (data) => mobileApiClient.post('/Translation/addTranslation', data),
  mobileUpdateTranslation: (id, data) => mobileApiClient.put(`/Translation/updateTranslation/${id}`, data)
}

// Storage Configuration API
export const storageAPI = {
  // Get storage configuration
  getStorageConfig: () => mobileApiClient.get('/storage/config'),
  
  // Get storage status
  getStorageStatus: () => mobileApiClient.get('/storage/status'),
  
  // Get available storage methods
  getAvailableMethods: () => mobileApiClient.get('/storage/methods')
}

// Export API clients for use in other services
export { apiClient, mobileApiClient }

export default {
  auth: authAPI,
  leave: leaveAPI,
  timesheet: timesheetAPI,
  recruitment: recruitmentAPI,
  employee: employeeAPI,
  notification: notificationAPI,
  dashboard: dashboardAPI,
  document: documentAPI,
  standardData: standardDataAPI,
  file: fileAPI,
  translation: translationAPI,
  storage: storageAPI
}